import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../services/api';
import './AchievementMetrics.css';

/**
 * AchievementMetrics - A modular component for displaying achievement metrics
 * Designed to be reusable for different special list types
 * 
 * @param {string} listId - The ID of the list to show metrics for
 * @param {string} listType - The type of list (e.g., 'dailyhabits')
 * @param {function} onRefresh - Optional callback when metrics are refreshed
 */
export default function AchievementMetrics({ listId, listType, onRefresh }) {
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadMetrics = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await api.getAchievementMetrics(listId);
      setMetrics(data);
    } catch (err) {
      console.error('Error loading achievement metrics:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [listId]);

  useEffect(() => {
    if (listType === 'dailyhabits') {
      loadMetrics();
    }
  }, [listId, listType, loadMetrics]);

  // Only render for dailyhabits lists
  if (listType !== 'dailyhabits') {
    return null;
  }

  if (loading) {
    return (
      <div className="achievement-metrics">
        <div className="achievement-metrics-loading">Loading metrics...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="achievement-metrics">
        <div className="achievement-metrics-error">Error loading metrics</div>
      </div>
    );
  }

  if (!metrics || !metrics.tasks || metrics.tasks.length === 0) {
    return null;
  }

  return (
    <div className="achievement-metrics">
      <div className="achievement-metrics-header">Task Streaks</div>
      <div className="achievement-metrics-content">
        {metrics.tasks.map((task, index) => {
          const streak = task.current_streak || 0;
          const yearRatio = task.year_ratio || 0;
          const yearPercentage = Math.round(yearRatio * 100);
          
          return (
            <div key={index} className="achievement-metric-task">
              <div className="achievement-metric-task-name">{task.text}</div>
              <div className="achievement-metric-task-stats">
                <div className="achievement-metric">
                  <div className="achievement-metric-label">Streak</div>
                  <div className="achievement-metric-value">{streak} {streak === 1 ? 'day' : 'days'}</div>
                </div>
                <div className="achievement-metric">
                  <div className="achievement-metric-label">Year</div>
                  <div className="achievement-metric-value">{yearPercentage}%</div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

